/**
 * To use the `profile-card` composable, add the `rc-profile-card` class to an <a> tag
 * and make sure that the href points to a study participant profile.
 *
 * If new elements are added with the `rc-profile-card` class after page initialization, the `initProfileCards`
 * method needs to be called.
 * */
import { useContext } from "@/store/context";
import { storeToRefs } from "pinia";
import { normalizeStyle, normalizeClass, h } from "vue";
// Composable
export const useProfileCard = () => {
    const { viewProfiles, currentStudyAlias } = storeToRefs(useContext());
    // Needs to be called any time new elements are created with the `rc-profile-card` class.
    // Omit `studyAlias` to gate on the current study — resolved synchronously from /context.
    const initProfileCards = (studyAlias) => {
        if (canViewProfile(studyAlias ?? currentStudyAlias.value))
            window.initProfileCards();
    };
    const getUserDisplayNameEl = (user, attrs, studyAlias) => {
        const makeLink = canViewProfile(studyAlias);
        let el;
        if (makeLink && user.profilePath) {
            el = document.createElement("a");
            el.href = user.profilePath;
            el.target = "_blank";
            el.classList.add("rc-profile-card");
        }
        else {
            el = document.createElement("span");
        }
        if (attrs.class)
            el.classList.add(normalizeClass(attrs.class));
        if (attrs.style)
            el.style.setProperty("cssText", normalizeStyle(attrs.style));
        if (attrs.innerHTML)
            el.innerHTML = attrs.innerHTML;
        else
            el.innerText = user.displayName;
        return el;
    };
    const canViewProfile = (studyAlias) => {
        return !!studyAlias && !!viewProfiles.value[studyAlias];
    };
    const UserDisplayName = (props, context) => {
        const studyAlias = props.studyAlias;
        const hasLink = !!props.user.profilePath;
        const canView = hasLink && studyAlias && canViewProfile(studyAlias);
        const el = canView ? "a" : "span";
        return h(el, {
            ...props,
            ...context.attrs,
            href: canView ? props.user.profilePath : undefined,
            target: canView ? "_blank" : undefined,
            class: canView ? normalizeClass(["rc-profile-card", context.attrs.class]) : context.attrs.class
        }, context.slots);
    };
    UserDisplayName.props = {
        user: { type: Object, required: true },
        studyAlias: { type: String, required: false }
    };
    return {
        initProfileCards,
        getUserDisplayNameEl,
        UserDisplayName,
        canViewProfile
    };
};
